import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

interface AiSidebarContextValue {
  isOpen: boolean
  toggle: () => void
  open: () => void
  close: () => void
  activeCampId: string | null
  setActiveCampId: (id: string | null) => void
}

const AiSidebarContext = createContext<AiSidebarContextValue | null>(null)

export function useAiSidebar(): AiSidebarContextValue {
  const ctx = useContext(AiSidebarContext)
  if (!ctx) {
    throw new Error('useAiSidebar must be used within an AiSidebarProvider')
  }
  return ctx
}

export function AiSidebarProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    return localStorage.getItem('ai-sidebar-open') === 'true'
  })
  const [activeCampId, setActiveCampId] = useState<string | null>(null)

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev
      localStorage.setItem('ai-sidebar-open', String(next))
      return next
    })
  }, [])

  const open = useCallback(() => {
    setIsOpen(true)
    localStorage.setItem('ai-sidebar-open', 'true')
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    localStorage.setItem('ai-sidebar-open', 'false')
  }, [])

  return (
    <AiSidebarContext.Provider value={{ isOpen, toggle, open, close, activeCampId, setActiveCampId }}>
      {children}
    </AiSidebarContext.Provider>
  )
}
